import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CACHE_TIME } from '@hygge/shared';
import {
  getExtensions,
  getExtension,
  enableExtension,
  disableExtension,
  updateExtensionSettings,
} from '@/lib/extensions';
import { useExtensionsStore } from '@/stores/extensions';

export const extensionsKeys = {
  all: ['extensions'] as const,
  list: () => [...extensionsKeys.all, 'list'] as const,
  detail: (id: string) => [...extensionsKeys.all, 'detail', id] as const,
};

export function useExtensions() {
  return useQuery({
    queryKey: extensionsKeys.list(),
    queryFn: getExtensions,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useExtension(id: string) {
  return useQuery({
    queryKey: extensionsKeys.detail(id),
    queryFn: () => getExtension(id),
    staleTime: CACHE_TIME.DEFAULT,
  });
}

export function useEnableExtension() {
  const queryClient = useQueryClient();
  const reloadStore = useExtensionsStore((s) => s.load);

  return useMutation({
    mutationFn: (id: string) => enableExtension(id),
    meta: { suppressGlobalToast: true },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: extensionsKeys.list() });
      queryClient.invalidateQueries({ queryKey: extensionsKeys.detail(id) });
      reloadStore();
    },
  });
}

export function useDisableExtension() {
  const queryClient = useQueryClient();
  const reloadStore = useExtensionsStore((s) => s.load);

  return useMutation({
    mutationFn: (id: string) => disableExtension(id),
    meta: { suppressGlobalToast: true },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: extensionsKeys.list() });
      queryClient.invalidateQueries({ queryKey: extensionsKeys.detail(id) });
      reloadStore();
    },
  });
}

export function useUpdateExtensionSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, settings }: { id: string; settings: Record<string, unknown> }) =>
      updateExtensionSettings(id, settings),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: extensionsKeys.list() });
      queryClient.invalidateQueries({ queryKey: extensionsKeys.detail(id) });
    },
  });
}
